<template>
	<div id="app-sidebar-2" class="sidebar-container hidden lg:block flex-shrink-0 z-1 select-none h-full">
		<div class="flex flex-column h-full">
			<!-- Header Section -->
			<div class="sidebar-header">
				<!-- Back to Users Button -->
				<div class="px-3 py-2">
					<Button 
						@click="toUsers" 
						icon="pi pi-chevron-left"
						label="Back to Users"
						class="sidebar-back-button" 
						text
						severity="secondary"
					/>
				</div>
				
				<!-- Title Section -->
				<div class="flex align-items-center gap-3 px-4 py-3 pb-4">
					<div class="header-icon-container">
						<i class="fa fa-user-circle text-white text-xl"></i>
					</div>
					<h4 class="font-bold text-gray-800 m-0">User Profile</h4>
				</div>
			</div>
			
			<!-- Menu Items -->
			<div class="overflow-y-auto sidebar-menu pt-2">
				<ul class="list-none p-2 m-0">
					<li v-for="option in config.options" :key="option.title" class="sidebar-menu-item">
						<router-link 
							:to="'/user/' + userId + option.slug" 
							class="sidebar-link"
						>
							<div class="flex align-items-center gap-3 py-2 px-1">
								<div class="menu-icon-container" :class="getIconColorClass(option.title)">
									<i :class="option.icon" class="menu-icon"></i>
								</div>
								<span class="font-medium menu-text">{{ option.title }}</span>
								<span v-if="option.label" class="menu-badge">
									{{ option.label }}
								</span>
							</div>
						</router-link>
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';

import { useRouter } from 'vue-router';

export default {
	props: ['userId'],
	setup() {
		const loading = ref(false);

		const router = useRouter();

		function toUsers() {
			router.push({ name: 'users' });
		}

		function getIconColorClass(title) {
			const colorMap = {
				'General': 'icon-blue',
				'Consents': 'icon-green',
				'Membership': 'icon-purple',
				'Member Cards': 'icon-indigo',
				'Notifications': 'icon-cyan',
				'Perks': 'icon-orange',
				'Permissions': 'icon-teal',
				'Event log': 'icon-yellow',
				'Danger Zone': 'icon-red',
			};
			return colorMap[title] || 'icon-gray';
		}

		async function loadRequiredData() {
			loading.value = true;

			loading.value = false;
		}

		onMounted(async () => {
			await loadRequiredData();
		});

		return {
			loading,

			toUsers,
			getIconColorClass,

			config: computed(() => {
				const options = [
					{
						title: 'General',
						icon: 'pi pi-info-circle',
						slug: '/general',
					},
					{
						title: 'Consents',
						icon: 'pi pi-check-square',
						slug: '/consents',
					},
					// {
					//     title: 'Country',
					//     icon: 'pi pi-flag',
					//     slug: '/country'
					// },
					{
						title: 'Membership',
						icon: 'pi pi-trophy',
						slug: '/billwerk',
					},
					// {
					// 	title: 'Stripe Membership (old)',
					// 	icon: 'pi pi-id-card',
					// 	slug: '/membership',
					// },

					{
						title: 'Member Cards',
						icon: 'pi pi-credit-card',
						slug: '/member-cards',
					},
					//   {
					//   title: "Receipts",
					//   icon: "fas fa-receipt",
					//   slug: "/receipts"
					// },
					{
						title: 'Notifications',
						icon: 'pi pi-inbox',
						slug: '/notifications',
					},
					{
						title: 'Perks',
						icon: 'pi pi-ticket',
						slug: '/perks',
					},
					{
						title: 'Permissions',
						icon: 'pi pi-lock',
						slug: '/permissions',
					},
					{
						title: 'Event log',
						icon: 'pi pi-calendar',
						slug: '/event-log',
					},
					{
						title: 'Danger Zone',
						icon: 'pi pi-ban',
						slug: '/danger-zone',
					},
				];

				return {
					options,
				};
			}),
		};
	},
};
</script>

<style scoped>
/* Sidebar container */
.sidebar-container {
  width: 280px;
  background: white;
  border-right: 1px solid #e5e7eb;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
}

/* Header section */
.sidebar-header {
  padding: 0;
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
}

.header-icon-container {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  padding: 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Back button styling */
.sidebar-back-button {
  width: 100% !important;
  justify-content: flex-start !important;
  color: #4b5563 !important;
}

.sidebar-back-button:hover {
  background: rgba(59, 130, 246, 0.1) !important;
  transform: translateX(-2px);
}

/* Menu section */
.sidebar-menu {
  padding: 0.5rem 0;
}

.sidebar-menu-item {
  margin-bottom: 0.125rem;
}

.sidebar-link {
  text-decoration: none;
  display: block;
  border-radius: 0.5rem;
  padding: 0 0.5rem;
  transition: all 0.2s ease;
}

.sidebar-link:hover {
  background: rgba(59, 130, 246, 0.08);
}

/* Icon containers with different colors */
.menu-icon-container {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.icon-blue {
  background: #dbeafe;
}

.icon-blue .menu-icon {
  color: #2563eb;
}

.icon-green {
  background: #d1fae5;
}

.icon-green .menu-icon {
  color: #059669;
}

.icon-purple {
  background: #e9d5ff;
}

.icon-purple .menu-icon {
  color: #7c3aed;
}

.icon-indigo {
  background: #e0e7ff;
}

.icon-indigo .menu-icon {
  color: #4f46e5;
}

.icon-cyan {
  background: #cffafe;
}

.icon-cyan .menu-icon {
  color: #0891b2;
}

.icon-orange {
  background: #fed7aa;
}

.icon-orange .menu-icon {
  color: #ea580c;
}

.icon-teal {
  background: #ccfbf1;
}

.icon-teal .menu-icon {
  color: #0d9488;
}

.icon-yellow {
  background: #fef3c7;
}

.icon-yellow .menu-icon {
  color: #ca8a04;
}

.icon-red {
  background: #fee2e2;
}

.icon-red .menu-icon {
  color: #dc2626;
}

.icon-gray {
  background: #f3f4f6;
}

.icon-gray .menu-icon {
  color: #6b7280;
}

.menu-icon {
  font-size: 1rem;
  transition: transform 0.2s ease;
}

.sidebar-link:hover .menu-icon {
  transform: scale(1.1);
}

.menu-text {
  flex: 1;
  color: #1f2937;
  transition: all 0.2s ease;
}

.sidebar-link:hover .menu-text {
  color: #111827;
  font-weight: 600;
}

/* Badge styling */
.menu-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  min-width: 1.5rem;
  height: 1.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Active state */
:deep(.router-link-exact-active) {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%) !important;
  border-left: 4px solid #1e40af;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
  margin: 0.25rem 0;
}

:deep(.router-link-exact-active > div) {
  background: transparent !important;
}

:deep(.router-link-exact-active .menu-text) {
  font-weight: 700 !important;
  color: white !important;
}

:deep(.router-link-exact-active .menu-icon-container) {
  background: rgba(255, 255, 255, 0.2) !important;
}

:deep(.router-link-exact-active .menu-icon) {
  color: white !important;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .sidebar-container {
    width: 240px;
  }
}
</style>
